import { ProjectCombobox } from "@/components/projects/ProjectCombobox";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useProjects, type Project } from "../hooks/useProjects";
// ProjectContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useProjects as useProjectSelection } from "../../../contexts/ProjectContext";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/button";
import { useMCPQueryScope } from "../hooks/useMCPQueryScope";
import { getKeycloakUserInfo } from "@/services/keycloak";
import { getActiveOrganizationId } from "@/utils/requestScope";

type Props = {
  value: string;
  onChange: (projectId: string) => void;
  triggerClassName?: string;
};

const projectLabel = (project: Project): string =>
  project.name ?? project.displayName ?? project.id;

const ProjectSelect = ({ value, onChange, triggerClassName }: Props) => {
  const { t, i18n } = useTranslation();
  const query = useProjects();
  const scope = useMCPQueryScope();
  const { data, isLoading, isError, isFetching, isPending } = query;
  const invalidated = query.isInvalidated;
  const ready = query.isSuccess && !isError && !isLoading && !isFetching && !invalidated && !query.isPlaceholderData;
  const projects = useMemo(() => data ?? [], [data]);
  const selected = projects.find((project) => project.id === value);
  const { selectedProjects } = useProjectSelection();
  const containerRef = useRef<HTMLDivElement>(null);
  type Opening = { id: number; scope: typeof scope; parent: HTMLElement | null };
  const [popup, setPopup] = useState<{ open: boolean; opening: Opening | null }>({ open: false, opening: null });
  const focusOwner = useRef<Opening | null>(null);
  useLayoutEffect(() => { focusOwner.current = popup.opening; }, [popup.opening]);
  useLayoutEffect(() => () => { focusOwner.current = null; }, []);

  const isCurrent = (owner: typeof scope) => owner.enabled && owner.active &&
    !owner.controller.signal.aborted && getKeycloakUserInfo()?.id === owner.key.principalId &&
    getActiveOrganizationId() === owner.requestScope.organizationId;
  // Reconcile before committing the next popup: disabling a trigger alone
  // would leave an already-open, now-empty Radix focus scope mounted.
  if (popup.open && (!ready || popup.opening?.scope !== scope)) {
    setPopup({ ...popup, open: false });
  }
  const restoreFocus = (event: Event) => {
    event.preventDefault();
    const opening = popup.opening;
    const container = containerRef.current;
    if (!opening || focusOwner.current !== opening || !isCurrent(opening.scope) || !container?.isConnected) return;
    const parent = opening.parent;
    if (parent && (!parent.isConnected || parent.dataset.state !== "open" || !parent.contains(container))) return;
    if (Array.from(document.querySelectorAll('[role="dialog"][data-state="open"], [role="listbox"][data-state="open"]'))
      .some((element) => element !== parent)) return;
    // Normal dismissal returns to the trigger. Forced unavailable-state
    // closure uses Retry, or the current dialog while a request is loading.
    for (const candidate of [container.querySelector<HTMLElement>('[data-slot="select-trigger"]'), container.querySelector<HTMLElement>('[data-project-retry]'), parent, container]) {
      if (!candidate?.isConnected || candidate.matches(":disabled, [aria-disabled='true']") ||
        candidate.closest("[hidden], [inert], [aria-hidden='true']")) continue;
      const style = getComputedStyle(candidate);
      if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") continue;
      candidate.focus();
      if (document.activeElement === candidate) break;
    }
  };

  // A connector can only be scoped to a project the user is currently working
  // in: offer the app-wide project selection, or every project when that
  // selection is "all". The connection's saved project stays listed either way
  // so editing one scoped to a project outside the selection doesn't blank the
  // field or silently drop its scope on save.
  const options = useMemo<Project[]>(() => {
    const selection: string[] = Array.isArray(selectedProjects)
      ? selectedProjects
      : [];
    if (selection.length === 0 || selection.includes("all")) return projects;
    const allowed = new Set(selection);
    return projects.filter(
      (project) => allowed.has(project.id) || project.id === value,
    );
  }, [projects, selectedProjects, value]);

  return (
    <div ref={containerRef} tabIndex={-1} dir={i18n.dir()}>
    <ProjectCombobox
      scopeKey={JSON.stringify(scope.key)}
      options={ready ? options.map((project) => ({ id: project.id, name: projectLabel(project) })) : []}
      selectedLabel={selected ? projectLabel(selected) : undefined}
      label={t("mcpPage.fields.project")}
      emptyMessage={t("mcpPage.noProjects")}
      contentKey={popup.opening?.id ?? 0}
      onCloseAutoFocus={restoreFocus}
      className={cn("h-10 w-full rounded-[8px] border-border bg-card text-foreground", triggerClassName)}
      placeholder={isLoading ? t("mcpPage.loadingProjects") : t("mcpPage.selectProject")}
      disabled={!ready}
      open={popup.open && ready && popup.opening?.scope === scope}
      onOpenChange={(open) => {
        if (open) {
          if (!ready || !isCurrent(scope)) return;
          setPopup((previous) => ({ open: true, opening: { id: (previous.opening?.id ?? 0) + 1, scope, parent: containerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null } }));
        } else setPopup((previous) => ({ ...previous, open: false }));
      }}
      value={selected ? value : ""}
      onValueChange={(projectId) => {
        if (ready && popup.opening?.scope === scope && isCurrent(scope)) onChange(projectId);
      }}
    />
    {(isPending || isFetching) && !isError && <p role="status" className="mt-1 text-xs text-muted-foreground">{t("mcpPage.loadingProjects")}</p>}
    {invalidated && !isPending && !isFetching && !isError && <p role="status" className="mt-1 text-xs text-muted-foreground">{t("mcpPage.refreshOptions")}</p>}
    {isError && <p role="alert" className="mt-1 text-xs text-destructive">{t("mcpPage.failedProjects")}</p>}
    {(isError || invalidated) && <Button data-project-retry type="button" variant="outline" className="mt-2 h-9" aria-disabled={isFetching} onClick={() => {
      if (scope.enabled && scope.active && !scope.controller.signal.aborted &&
        getKeycloakUserInfo()?.id === scope.key.principalId &&
        getActiveOrganizationId() === scope.requestScope.organizationId) {
        void query.refetch({ cancelRefetch: false });
      }
    }}>{t("common.retry")}</Button>}
    </div>
  );
};

export default ProjectSelect;
